// Centralized contact info — single source of truth for the frontend.
// Numbers are stored in international format (E.164 without the '+') so wa.me URLs work.
//
// iter53 — the CALL number is now ALSO live-editable from Admin → Settings
// → "Contact number". The static value below is the launch default; once
// the admin saves a different value, components that consume the hook
// `useContactNumber()` will swap in the live value automatically. Anything
// still importing CONTACT.CALL directly (legacy / static pages) keeps using
// the default — same behaviour as before.
import { useSettings } from "../contexts/SettingsContext";

export const CONTACT = {
  WHATSAPP: "916281149443",         // For wa.me links
  WHATSAPP_DISPLAY: "+91 62811 49443",
  CALL: "919391688723",              // For tel: links
  CALL_DISPLAY: "+91 93916 88723",
  EMAIL: "sridurgafoodssupport@gmail.com",
  ADDRESS: "Nellore, Andhra Pradesh, India",
};

export const waUrl = (msg = "") =>
  `https://wa.me/${CONTACT.WHATSAPP}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

// Pre-filled message for product enquiries (questions, not orders).
export const WA_ENQUIRY_MSG = "Hi, I have a few questions regarding your products.";
export const waEnquiryUrl = () => waUrl(WA_ENQUIRY_MSG);

export const telUrl = (raw) => `tel:+${(raw || CONTACT.CALL).replace(/[^0-9]/g, "")}`;
export const mailUrl = (subject = "") =>
  `mailto:${CONTACT.EMAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;

/**
 * Format a raw E.164-ish phone number into "+91 XXXXX XXXXX" style for
 * display. Falls back to the input when the shape is unexpected.
 */
function formatPhone(raw) {
  const digits = (raw || "").replace(/[^0-9]/g, "");
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return raw || CONTACT.CALL_DISPLAY;
}

/**
 * Hook — returns the live, admin-editable contact number plus a pre-built
 * tel: URL. Use this in components that should reflect changes saved in
 * Admin → Settings without needing a redeploy.
 */
export function useContactNumber() {
  const { settings } = useSettings();
  const raw = (settings?.contact_number || "").trim() || CONTACT.CALL;
  return {
    raw,
    display: formatPhone(raw),
    tel: telUrl(raw),
  };
}

/**
 * iter71 — Hook returning the live, admin-editable Customer Contact Email
 * plus a pre-built `mailto:` URL builder.  Use this instead of the static
 * CONTACT.EMAIL in every component that displays or links to the customer
 * email so a single Admin → Settings save propagates everywhere.
 */
export function useContactEmail() {
  const { settings } = useSettings();
  const address = (settings?.contact_email || "").trim() || CONTACT.EMAIL;
  return {
    address,
    mailto: (subject = "") =>
      `mailto:${address}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`,
  };
}

/**
 * iter72 — Hook returning the live, admin-editable Business Hours line
 * (e.g. "Mon – Sat | 10:00 AM – 6:00 PM").  Displayed subtly below the
 * contact-details block in the Footer and Contact page.
 */
export function useBusinessHours() {
  const { settings } = useSettings();
  return (settings?.business_hours || "").trim() || "Mon – Sat | 10:00 AM – 6:00 PM";
}
